import { useQuery } from "@tanstack/react-query";
import type {
  PanchangDay,
  PanchangRangeResponse,
  PanchangStatus,
  SankalpaCoordinates,
} from "@vedaunion/shared";
import { apiFetch } from "./api";

// -----------------------------------------------------------------------------
// React Query hooks for the Vedic calendar (panchāṅga). All reads are served
// from the server's DB cache; the client never drives the Prokerala API.
// -----------------------------------------------------------------------------

const HOUR = 1000 * 60 * 60;

export interface PanchangLocation {
  lat: number;
  lng: number;
  tz: string;
  place: string;
}

function locationParams(loc?: PanchangLocation | null): string {
  if (!loc) return "";
  return `?lat=${loc.lat}&lng=${loc.lng}&tz=${encodeURIComponent(loc.tz)}&place=${encodeURIComponent(loc.place)}`;
}

/** Whether the integration is configured (drives showing the overlay at all). */
export function usePanchangStatus() {
  return useQuery<PanchangStatus>({
    queryKey: ["panchang", "status"],
    queryFn: ({ signal }) => apiFetch<PanchangStatus>("/api/panchang/status", { signal }),
    staleTime: 12 * HOUR,
  });
}

/** Today's panchang. Polls every hour so the "right now" view stays fresh. */
export function usePanchangToday(enabled = true, location?: PanchangLocation | null) {
  const params = locationParams(location);
  return useQuery<{ day: PanchangDay }>({
    queryKey: ["panchang", "today", location ?? null],
    queryFn: ({ signal }) =>
      apiFetch<{ day: PanchangDay }>(`/api/panchang/today${params}`, { signal }),
    enabled,
    staleTime: HOUR,
    refetchInterval: HOUR,
  });
}

/** A specific day (YYYY-MM-DD). */
export function usePanchangDay(
  date: string | undefined,
  enabled = true,
  location?: PanchangLocation | null,
) {
  const params = locationParams(location);
  return useQuery<{ day: PanchangDay }>({
    queryKey: ["panchang", "day", date, location ?? null],
    queryFn: ({ signal }) =>
      apiFetch<{ day: PanchangDay }>(`/api/panchang/${date}${params}`, { signal }),
    enabled: enabled && !!date,
    staleTime: 6 * HOUR,
  });
}

/** Saṅkalpa time/place coordinates for a date + location. */
export function useSankalpaCoordinates(
  date: string | undefined,
  enabled = true,
  location?: PanchangLocation | null,
) {
  const base = locationParams(location);
  const params = base
    ? `${base}&date=${date}`
    : `?date=${date}`;
  return useQuery<{ coordinates: SankalpaCoordinates }>({
    queryKey: ["sankalpa", "coordinates", date, location ?? null],
    queryFn: ({ signal }) =>
      apiFetch<{ coordinates: SankalpaCoordinates }>(`/api/sankalpa/coordinates${params}`, { signal }),
    enabled: enabled && !!date,
    staleTime: 6 * HOUR,
  });
}

/** Compact markers for a date range (month-grid overlay). */
export function usePanchangRange(
  from: string,
  to: string,
  enabled = true,
  location?: PanchangLocation | null,
) {
  const locPart = location
    ? `&lat=${location.lat}&lng=${location.lng}&tz=${encodeURIComponent(location.tz)}&place=${encodeURIComponent(location.place)}`
    : "";
  return useQuery<PanchangRangeResponse>({
    queryKey: ["panchang", "range", from, to, location ?? null],
    queryFn: ({ signal }) =>
      apiFetch<PanchangRangeResponse>(`/api/panchang?from=${from}&to=${to}${locPart}`, { signal }),
    enabled,
    staleTime: 6 * HOUR,
  });
}
